import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTO_HIDE_DELAY_MS } from './constants';

/**
 * 停止/暂停后自动隐藏（并支持交互重置倒计时）
 *
 * 规则：
 * - playing=true：永远显示，清除计时器（开始播放歌曲时停止自动隐藏倒计时）
 * - playing=false 且 !isFullscreen 且 !suspend：开始 delayMs 倒计时，超时置 autoHidden=true
 *   （手动暂停播放歌曲和歌曲播放完毕后，如果播放器不是Hover状态，启动自动隐藏倒计时）
 * - suspend=true：暂停倒计时（鼠标在播放器内部时）
 * - bump()：立刻显示（autoHidden=false）并重置倒计时
 */
export function useAutoHideOnStop({ playing, isFullscreen, delayMs = AUTO_HIDE_DELAY_MS, suspend = false }) {
  const [autoHidden, setAutoHidden] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    const id = timerRef.current;
    if (id) {
      try { clearTimeout(id); } catch (_) {}
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (playing || isFullscreen || suspend) return;
    timerRef.current = setTimeout(() => {
      setAutoHidden(true);
    }, delayMs);
  }, [clearTimer, delayMs, playing, isFullscreen, suspend]);

  const bump = useCallback(() => {
    if (playing || isFullscreen || suspend) return;
    setAutoHidden(false);
    startTimer();
  }, [playing, isFullscreen, suspend, startTimer]);

  useEffect(() => {
    if (playing || isFullscreen || suspend) {
      setAutoHidden(false);
      clearTimer();
      return;
    }
    setAutoHidden(false);
    startTimer();
    return () => clearTimer();
  }, [playing, isFullscreen, suspend, startTimer, clearTimer]);

  return { autoHidden, bump, setAutoHidden, clearTimer, startTimer };
}

